import { useState, useCallback, useContext } from 'react';
import { ApiClientService, useApiClient } from '../../api_client';
import { AuthContext } from '../../auth/contexts/auth.context';
export const useReauthentication = (options) => {
    const { user, getIDPConfiguration, ssoLogin, lastEntities } = useContext(AuthContext);
    const iamClient = useApiClient(ApiClientService.IAM);
    const [isReauthenticating, setIsReauthenticating] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordError, setPasswordError] = useState(null);
    const reauthenticate = useCallback(async () => {
        if (!user?.email) {
            options.onError('User not logged in');
            return;
        }
        setIsReauthenticating(true);
        setPasswordError(null);
        try {
            const idpList = await getIDPConfiguration(user.email);
            if (!idpList || idpList.length === 0) {
                throw new Error('No authentication method configured');
            }
            // Find available authentication methods
            const passwordIdp = idpList.find((idp) => idp.loginMethods.includes('password'));
            const ssoIdp = idpList.find((idp) => idp.loginMethods.includes('sso'));
            // For now, prefer password authentication (shows modal)
            // SSO flow will be handled when user returns from IAM
            if (passwordIdp) {
                setShowPasswordModal(true);
                setIsReauthenticating(false);
            }
            else if (ssoIdp) {
                // Redirect to SSO - token will be in cookie when user returns
                ssoLogin(ssoIdp, user.email, options.txn);
            }
            else {
                throw new Error('No supported authentication method found');
            }
        }
        catch (err) {
            setIsReauthenticating(false);
            options.onError(err.message || 'Re-authentication failed');
        }
    }, [user, getIDPConfiguration, ssoLogin, options]);
    const handlePasswordSubmit = useCallback(async (password, printedName) => {
        if (!user?.email) {
            return;
        }
        setIsReauthenticating(true);
        setPasswordError(null);
        try {
            // Call IAM /v1/login to verify password and get fresh token
            // Use loginEntity to maintain session context
            const lastEntity = lastEntities.find((e) => e.email === user.email);
            const loginEntityId = lastEntity?.entityId || 1;
            const loginResponse = await iamClient.post('/v1/login', {
                email: user.email,
                password,
                entityId: loginEntityId,
                ...(options.txn && { txn: options.txn }),
            });
            // Get token from response body (password authentication returns it directly)
            const finalToken = loginResponse.data?.token;
            if (!finalToken) {
                throw new Error('No token received from IAM login');
            }
            setShowPasswordModal(false);
            setIsReauthenticating(false);
            options.onSuccess(finalToken, printedName);
        }
        catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Invalid password. Please try again.';
            setPasswordError(errorMessage);
            setIsReauthenticating(false);
        }
    }, [user, lastEntities, iamClient, options]);
    const closePasswordModal = useCallback(() => {
        setShowPasswordModal(false);
        setIsReauthenticating(false);
        setPasswordError(null);
        options.onCancel?.();
    }, [options]);
    return {
        reauthenticate,
        isReauthenticating,
        showPasswordModal,
        closePasswordModal,
        handlePasswordSubmit,
        passwordError,
    };
};
